import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  MouseEvent,
  ReactElement
} from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import { 
  useMenuItemsMonthlyTrendQuery
} from '../../hooks/queries/useDashboardQuery';
import { useResponsive } from '../../hooks/useResponsive';
import MonthlyItemSalesChart from './MonthlyItemSalesChart';
import './MenuCategoryTrendChart.css';


interface MenuCategoryMonthlyData {
  year_month: string;
  categories: {
    [key: string]: {
      menu_group: string;
      menu_category: string;
      gross_sales: string;
      total_refunds: string;
      net_sales: string;
      order_count: number;
      quantity: number;
    };
  };
}

interface MenuItemsMonthlyData {
  year_month: string;
  items: {
    [key: string]: {
      menu_item: string;
      gross_sales: string;
      total_refunds: string;
      net_sales: string;
      order_count: number;
      quantity: number;
    };
  };
}

interface MenuItemsMonthlyResponse {
  activeItems: string[];
  monthlyData: MenuItemsMonthlyData[];
}

interface MenuCategory {
  menu_group: string;
  menu_category: string;
  total_sales: string;
}


interface GroupedCategories {
  [groupName: string]: MenuCategory[];
}

interface ModalPosition {
  y: number;
}



const extractNumber = (v: string | number): number =>
  typeof v === 'string' ? parseFloat(v.replace(/[^0-9.-]+/g, '')) : v || 0;

const formatMonth = (ym: string): string => {
  const [y, m] = ym.split('-');
  return `${y}/${m}`;
};

const calcRange = (vals: number[]): { min: number; max: number } => {
  const valid = vals.filter(n => n > 0 && Number.isFinite(n));
  if (!valid.length) return { min: 0, max: 100 };
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const span = max - min;
  return {
    min: Math.max(0, Math.floor(min - span * 0.05)),
    max: Math.ceil(max + span * 0.05)
  };
};

const colors: string[] = [
  '#F97F76',  
  '#474646',  
  '#F9C27B',  
  '#4285F4',  
  '#34A853',  
  '#EA4335',  
  '#9C27B0',  
  '#FF9800',  
  '#795548',  
  '#607D8B',  
  '#E91E63',  
  '#00BCD4',  
  '#8BC34A',  
  '#FFC107',  
  '#673AB7',  
  '#009688'   
];


const MenuCategoryTrendChart = (): ReactElement => {
  const { t } = useTranslation();
  const { isLaptop, isiPad, isiPhone } = useResponsive();

  
  const { isFiltersApplying } = useFilters();

  
  const { data: dashboardData, loading, error, salesforceId } = useDashboardData();
  
  
  const catMonthlyData = (dashboardData?.chartData as any)?.categoryMonthlyTrend || [];
  const categories = (dashboardData?.chartData as any)?.menuCategories || [];

  
  const [selectedGroup, setSelectedGroup] = useState<string>(''); 
  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(new Set()); 
  const catChartRef = useRef<ReactECharts>(null);

  
  const [viewMode, setViewMode] = useState<'categories' | 'items'>('categories'); 
  const [selectedCategoryForItems, setSelectedCategoryForItems] = useState<{ group: string; category: string } | null>(null); 
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set()); 

  
  const [showItems, setShowItems] = useState<boolean>(false);
  const [selectedCat, setSelectedCat] = useState<{ group: string; category: string } | null>(null);
  const [itemsPos, setItemsPos] = useState<ModalPosition>({ y: 0 });
  
  
  const { 
    data: itemsData, 
    isLoading: trendLoading, 
    error: trendError 
  } = useMenuItemsMonthlyTrendQuery(
    salesforceId || '', 
    selectedCat?.group || '', 
    selectedCat?.category || '', 
    (showItems && !!selectedCat) || (viewMode === 'items' && !!selectedCat)
  );

  
  useEffect(() => {
    if (viewMode === 'items' && itemsData?.activeItems && itemsData.activeItems.length > 0) {
      const itemKeys = new Set(itemsData.activeItems as string[]);
      setVisibleItems(itemKeys);
    }
  }, [viewMode, itemsData]);

  
  const itemsChartRef = useRef<ReactECharts>(null);
  const [allItemsOn, setAllItemsOn] = useState<boolean>(true);

  
  const [showTable, setShowTable] = useState<boolean>(false);
  const [tablePos, setTablePos] = useState<ModalPosition>({ y: 0 });

  
  
  
  const groupedCategories = useMemo<GroupedCategories>(() => {
    const grouped: GroupedCategories = {};
    categories.forEach((category: MenuCategory) => {
      if (!grouped[category.menu_group]) {
        grouped[category.menu_group] = [];
      }
      grouped[category.menu_group].push(category);
    });
    return grouped;
  }, [categories]);

  
  const groupNames = useMemo(() => {
    return Object.keys(groupedCategories).sort();
  }, [groupedCategories]);

  
  useEffect(() => {
    if (!loading && groupNames.length > 0 && !selectedGroup) {
      
      const preferredGroup = groupNames.find(group => group.toLowerCase().includes('chicken')) || groupNames[0];
      setSelectedGroup(preferredGroup);
      
      
      const selectedGroupCategories = groupedCategories[preferredGroup] || [];
      const categoryKeys = new Set(selectedGroupCategories.map(cat => cat.menu_category));
      setVisibleCategories(categoryKeys);
    }
  }, [loading, groupNames, selectedGroup, groupedCategories]);

  
  
  
  const handleGroupChange = (groupName: string): void => {
    
    if (selectedGroup === groupName) {
      return;
    }
    
    setSelectedGroup(groupName);
    
    
    if (viewMode === 'items') {
      setViewMode('categories');
      setSelectedCategoryForItems(null);
      setSelectedCat(null);
      setVisibleItems(new Set());
    }
    
    
    const groupCategories = groupedCategories[groupName] || [];
    const categoryKeys = new Set(groupCategories.map(cat => cat.menu_category));
    setVisibleCategories(categoryKeys);
    
    
    if (catChartRef.current) {
      const chartInstance = catChartRef.current.getEchartsInstance();
      
      setTimeout(() => {
        chartInstance.clear();
        chartInstance.resize();
      }, 0);
    }
  };

  
  const toggleCategory = (categoryName: string): void => {
    const newVisible = new Set(visibleCategories);
    if (newVisible.has(categoryName)) {
      newVisible.delete(categoryName);
    } else {
      newVisible.add(categoryName);
    }
    setVisibleCategories(newVisible);

    
    if (catChartRef.current) {
      const inst = catChartRef.current.getEchartsInstance();
      inst.dispatchAction({
        type: newVisible.has(categoryName) ? 'legendSelect' : 'legendUnSelect',
        name: categoryName
      });
    }
  };

  
  const currentGroupCategories = useMemo(() => {
    return groupedCategories[selectedGroup] || [];
  }, [groupedCategories, selectedGroup]);

  
  
  
  const handleDrillDownToItems = (menuGroup: string, menuCategory: string): void => {
    setSelectedCategoryForItems({ group: menuGroup, category: menuCategory });
    setViewMode('items');
    
    
    setSelectedCat({ group: menuGroup, category: menuCategory });
  };

  
  const handleBackToCategories = (): void => {
    setViewMode('categories');
    setSelectedCategoryForItems(null);
    setSelectedCat(null);
    setVisibleItems(new Set());
  };

  
  const toggleItem = (itemName: string): void => {
    const newVisible = new Set(visibleItems);
    if (newVisible.has(itemName)) {
      newVisible.delete(itemName);
    } else {
      newVisible.add(itemName);
    }
    setVisibleItems(newVisible);

    
    if (catChartRef.current) {
      const inst = catChartRef.current.getEchartsInstance();
      inst.dispatchAction({
        type: newVisible.has(itemName) ? 'legendSelect' : 'legendUnSelect',
        name: itemName
      });
    }
  };

  
  const toggleAllItemsInSidebar = (): void => {
    if (!itemsData?.activeItems) return;
    
    const allItems = new Set(itemsData.activeItems as string[]);
    const areAllVisible = allItems.size > 0 && Array.from(allItems).every(item => visibleItems.has(item));
    
    if (areAllVisible) {
      
      setVisibleItems(new Set());
      
      if (catChartRef.current) {
        const inst = catChartRef.current.getEchartsInstance();
        (itemsData.activeItems as string[]).forEach((name: string) =>
          inst.dispatchAction({
            type: 'legendUnSelect',
            name
          })
        );
      }
    } else {
      
      setVisibleItems(allItems);
      
      if (catChartRef.current) {
        const inst = catChartRef.current.getEchartsInstance();
        (itemsData.activeItems as string[]).forEach((name: string) =>
          inst.dispatchAction({
            type: 'legendSelect',
            name
          })
        );
      }
    }
  };

  
  const fetchItemsTrend = (
    menuGroup: string,
    menuCategory: string,
    pos: ModalPosition
  ): void => {
    setItemsPos(pos);
    setSelectedCat({ group: menuGroup, category: menuCategory });
    setAllItemsOn(true);
    setShowItems(true);
  };

  const toggleAllItems = (): void => {
    if (!itemsChartRef.current || !itemsData) return;
    const inst = itemsChartRef.current.getEchartsInstance();
    (itemsData.activeItems as string[]).forEach((name: string) =>
      inst.dispatchAction({
        type: allItemsOn ? 'legendUnSelect' : 'legendSelect',
        name
      })
    );
    setAllItemsOn(!allItemsOn);
  };

  
  const catOption = useMemo(() => {
    if (!catMonthlyData.length || !currentGroupCategories.length) return {};
    
    const months = catMonthlyData.map((d: any) => formatMonth(d.year_month));
    const vals: number[] = [];

    
    const series = currentGroupCategories.map((c: MenuCategory, idx: number) => {
      const key = `${c.menu_group}|${c.menu_category}`;
      const data = catMonthlyData.map((m: any) =>
        m.categories[key] ? extractNumber(m.categories[key].net_sales) : 0
      );
      vals.push(...data.filter((n: number) => n > 0));
      
      return {
        name: c.menu_category,
        type: 'line',
        data,
        lineStyle: { 
          width: isiPhone ? 2 : (isiPad ? 2.5 : (isLaptop ? 2.5 : 3)), 
          color: colors[idx % colors.length] 
        },
        itemStyle: { color: colors[idx % colors.length] },
        symbol: 'circle',
        symbolSize: isiPhone ? 4 : (isiPad ? 5 : (isLaptop ? 5 : 6)),
        smooth: false
      };
    });

    const { min } = calcRange(vals);

    return {
      grid: { 
        left: '2%', 
        right: '2%', 
        top: '8%', 
        bottom: isiPad ? '24%' : '4%', 
        containLabel: true 
      },
      tooltip: { 
        trigger: 'axis', 
        axisPointer: { type: 'cross' },
        confine: true, 
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isiPhone ? 10 : (isiPad ? 11 : (isLaptop ? 11 : 12))
        },
        formatter: function(params: any) {
          if (!params || params.length === 0) return '';
          
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px; border-left: 4px solid #F97F76;">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '12px' : '13px'};">${monthStr}</div>`;
          
          const validParams = params.filter((param: any) => param.value > 0);
          
          validParams.forEach((param: any) => {
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 6px; border-radius: 50%;"></span>
                <strong>${param.seriesName}:</strong>
              </span>
              <span style="font-weight: bold; font-size: ${isLaptop ? '10px' : '11px'};">$${param.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>`;
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: { 
        show: false 
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: isiPhone ? 9 : (isiPad ? 10 : (isLaptop ? 11 : 12)),
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: isiPhone ? 5 : (isiPad ? 6 : 8)
        }
      },
      yAxis: {
        type: 'value',
        name: t('menuCategoryChart.yAxisMonthlySales'),
        min,
        scale: true,
        nameTextStyle: { 
          fontSize: isiPhone ? 9 : (isiPad ? 10 : (isLaptop ? 11 : 12)), 
          fontWeight: 'bold' 
        },
        axisLabel: { 
          fontSize: isiPhone ? 8 : (isiPad ? 9 : (isLaptop ? 10 : 11)), 
          formatter: '${value}' 
        },
        splitLine: { lineStyle: { opacity: 0.3 } }
      },
      series
    };
  }, [catMonthlyData, currentGroupCategories, t, isLaptop, isiPad, isiPhone]);

  const itemsOption = useMemo(() => {
    if (!itemsData?.monthlyData.length) return {};
    const months = itemsData.monthlyData.map((m: any) => formatMonth(m.year_month));
    const vals: number[] = [];

    const series = (itemsData.activeItems as string[]).map((item: string, idx: number) => {
      const data = itemsData.monthlyData.map((m: any) =>
        m.items[item] ? extractNumber(m.items[item].net_sales) : 0
      );
      vals.push(...data.filter((n: number) => n > 0));
      return {
        name: item,
        type: 'line',
        data,
        lineStyle: { width: isiPhone ? 1.5 : (isiPad ? 2 : (isLaptop ? 2 : 2.5)), color: colors[idx % colors.length] },
        itemStyle: { color: colors[idx % colors.length] },
        symbol: 'circle',
        symbolSize: isiPhone ? 3 : (isiPad ? 4 : (isLaptop ? 4 : 5)),
        smooth: false
      };
    });

    const { min } = calcRange(vals);

    return {
      grid: { left: '2%', right: '2%', top: '8%', bottom: isiPad ? '24%' : '4%', containLabel: true },
      legend: {
        show: false 
      },
      tooltip: { 
        trigger: 'axis', 
        axisPointer: { type: 'cross' },
        confine: true, 
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isiPhone ? 10 : (isiPad ? 11 : (isLaptop ? 11 : 12))
        },
        formatter: function(params: any) {
          if (!params || params.length === 0) return '';
          
          const monthStr = params[0].axisValue;
          const validParams = params.filter((param: any) => param.value > 0);
          
          if (validParams.length === 0) return `<div style="padding: 3px;"><strong>${monthStr}</strong><br/>No data</div>`;
          
          
          const itemsPerColumn = Math.ceil(validParams.length / 2);
          const shouldUseColumns = validParams.length > 6 && !isiPhone;
          
          
          const tooltipWidth = isiPhone ? 'min-width: 200px; max-width: 280px' : 
                              shouldUseColumns ? 'min-width: 400px; max-width: 600px' : 
                              'min-width: 280px; max-width: 600px';
          
          let html = `<div style="padding: ${isiPhone ? '6px' : '8px'}; border-left: 4px solid #F97F76; ${tooltipWidth};">
            <div style="font-weight: bold; margin-bottom: ${isiPhone ? '6px' : '8px'}; font-size: ${isiPhone ? '10px' : (isLaptop ? '12px' : '13px')}; text-align: center;">${monthStr}</div>`;
          
          if (shouldUseColumns) {
            html += '<div style="display: flex; gap: 20px;">';
            
            
            html += '<div style="flex: 1; min-width: 0;">';
            for (let i = 0; i < itemsPerColumn; i++) {
              if (validParams[i]) {
                const param = validParams[i];
                html += `<div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; min-height: 16px;">
                  <span style="display: flex; align-items: center; margin-right: 10px; flex: 1; min-width: 0;">
                    <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 6px; border-radius: 50%; flex-shrink: 0; margin-top: 2px;"></span>
                    <span style="font-size: ${isLaptop ? '9px' : '10px'}; font-weight: 500; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.3;">${param.seriesName}:</span>
                  </span>
                  <span style="font-weight: bold; font-size: ${isLaptop ? '9px' : '10px'}; flex-shrink: 0; margin-left: 5px;">$${param.value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>`;
              }
            }
            html += '</div>';
            
            
            if (validParams.length > itemsPerColumn) {
              html += '<div style="flex: 1; min-width: 0;">';
              for (let i = itemsPerColumn; i < validParams.length; i++) {
                const param = validParams[i];
                html += `<div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; min-height: 16px;">
                  <span style="display: flex; align-items: center; margin-right: 10px; flex: 1; min-width: 0;">
                    <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 6px; border-radius: 50%; flex-shrink: 0; margin-top: 2px;"></span>
                    <span style="font-size: ${isLaptop ? '9px' : '10px'}; font-weight: 500; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.3;">${param.seriesName}:</span>
                  </span>
                  <span style="font-weight: bold; font-size: ${isLaptop ? '9px' : '10px'}; flex-shrink: 0; margin-left: 5px;">$${param.value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>`;
              }
              html += '</div>';
            }
            
            html += '</div>';
          } else {
            
            validParams.forEach((param: any) => {
              const fontSize = isiPhone ? '8px' : (isLaptop ? '10px' : '11px');
              const marginBottom = isiPhone ? '3px' : '4px';
              const dotSize = isiPhone ? '6px' : '8px';
              const spacing = isiPhone ? '4px' : '6px';
              const marginRight = isiPhone ? '8px' : '15px';
              
              html += `<div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: ${marginBottom}; min-height: ${isiPhone ? '14px' : '16px'};">
                <span style="display: flex; align-items: center; margin-right: ${marginRight}; flex: 1; min-width: 0;">
                  <span style="display: inline-block; width: ${dotSize}; height: ${dotSize}; background-color: ${param.color}; margin-right: ${spacing}; border-radius: 50%; flex-shrink: 0; margin-top: 2px;"></span>
                  <strong style="font-size: ${fontSize}; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.2; ${isiPhone ? 'max-width: 120px; text-overflow: ellipsis; white-space: nowrap; overflow: hidden;' : ''}">${param.seriesName}:</strong>
                </span>
                <span style="font-weight: bold; font-size: ${fontSize}; flex-shrink: 0; margin-left: ${isiPhone ? '3px' : '5px'};">$${param.value.toLocaleString('en-US', { minimumFractionDigits: isiPhone ? 0 : 2, maximumFractionDigits: isiPhone ? 0 : 2 })}</span>
              </div>`;
            });
          }
          
          html += '</div>';
          return html;
        }
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: isLaptop ? 9 : 10,
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: isLaptop ? 5 : 6
        }
      },
      yAxis: {
        type: 'value',
        name: t('menuCategoryChart.yAxisMonthlySales'),
        min,
        scale: true,
        nameTextStyle: { fontSize: isiPhone ? 8 : (isiPad ? 9 : (isLaptop ? 10 : 11)), fontWeight: 'bold' },
        axisLabel: { fontSize: isiPhone ? 7 : (isiPad ? 8 : (isLaptop ? 9 : 10)), formatter: '${value}' },
        splitLine: { lineStyle: { opacity: 0.3 } }
      },
      series
    };
  }, [itemsData, t, isLaptop, isiPad, isiPhone]);

  
  const calcPos = (e: MouseEvent, height = 650): ModalPosition => {
    const y = e.clientY;
    const pad = 20;
    const h = window.innerHeight;
    let finalY = y;
    if (y + height + pad > h) finalY = h - height - pad;
    if (finalY < pad) finalY = pad;
    return { y: finalY };
  };

  
  const calcTablePos = (e: MouseEvent, height = 650): ModalPosition => {
    const y = e.clientY;
    const pad = 20;
    const h = window.innerHeight;
    let finalY = y - height; 
    if (finalY < pad) finalY = pad; 
    if (finalY + height + pad > h) finalY = h - height - pad; 
    return { y: finalY };
  };

  
  if (loading) {
    return (
      <div className="menu-category-trend-chart">
        <div className="menu-category-title-band">
          <h2 style={{
            fontSize: isiPhone ? '14px' : (isiPad ? '15px' : (isLaptop ? '16px' : '18px')),
            padding: isiPhone ? '6px 15px' : (isiPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
          }}>{t('menuCategoryChart.title')}</h2>
        </div>
        <div className="menu-category-chart-container">
          <div className="menu-category-loading">
            <div className="menu-category-loading-spinner" />
            <div className="menu-category-loading-text" style={{ 
              fontSize: isiPhone ? '12px' : (isiPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {t('menuCategoryChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = error || t('menuCategoryChart.loadingFailed', { message: 'Unknown error' });
    return (
      <div className="menu-category-trend-chart">
        <div className="menu-category-title-band">
          <h2 style={{
            fontSize: isiPhone ? '14px' : (isiPad ? '15px' : (isLaptop ? '16px' : '18px')),
            padding: isiPhone ? '6px 15px' : (isiPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
          }}>{t('menuCategoryChart.title')}</h2>
        </div>
        <div className="menu-category-chart-container">
          <div className="menu-category-error">
            <div className="menu-category-error-icon">⚠️</div>
            <div className="menu-category-error-text" style={{ 
              fontSize: isiPhone ? '12px' : (isiPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {errorMessage}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!loading && (!catMonthlyData.length || !categories.length)) {
    return (
      <div className="menu-category-trend-chart">
        <div className="menu-category-title-band">
          <h2 style={{
            fontSize: isiPhone ? '14px' : (isiPad ? '15px' : (isLaptop ? '16px' : '18px')),
            padding: isiPhone ? '6px 15px' : (isiPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
          }}>{t('menuCategoryChart.title')}</h2>
        </div>
        <div className="menu-category-chart-container">
          <div className="menu-category-error">
            <div className="menu-category-error-icon">📊</div>
            <div className="menu-category-error-text" style={{ 
              fontSize: isiPhone ? '12px' : (isiPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {t('menuCategoryChart.noData')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {}
      <div className="menu-category-trend-chart">
        <div className="menu-category-title-band">
          <h2 style={{
            fontSize: isiPhone ? '14px' : (isiPad ? '15px' : (isLaptop ? '16px' : '18px')),
            padding: isiPhone ? '6px 15px' : (isiPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
          }}>{t('menuCategoryChart.title')}</h2>
        </div>
        
        <div className="menu-category-chart-container">
          {}
          <div className="menu-category-tabs">
            {groupNames.map(groupName => (
              <button
                key={groupName}
                className={`menu-category-tab ${selectedGroup === groupName ? 'active' : ''}`}
                onClick={() => handleGroupChange(groupName)}
                style={{
                  fontSize: isLaptop ? '12px' : '14px',
                  padding: isLaptop ? '6px 12px' : '8px 16px',
                  margin: isLaptop ? '0 2px' : '0 4px'
                }}
              >
                {groupName}
                <span className="menu-category-tab-count">
                  ({groupedCategories[groupName]?.length || 0})
                </span>
              </button>
            ))}
          </div>

          {}
          <div className="menu-category-main-content">
            {}
            <div className="menu-category-sidebar">
              <div className="menu-category-sidebar-header">
                {viewMode === 'categories' ? (
                  <>
                    <h4 style={{ 
                      fontSize: isLaptop ? '13px' : '15px',
                      margin: '0 0 10px 0',
                      color: '#333'
                    }}>
                      {selectedGroup}
                    </h4>
                    <div style={{ 
                      fontSize: isLaptop ? '10px' : '11px',
                      color: '#666',
                      marginBottom: '15px'
                    }}>
                      {t('menuCategoryChart.chartHintLegend')}
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleBackToCategories}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#474646',
                        fontSize: isLaptop ? '11px' : '12px',
                        cursor: 'pointer',
                        marginBottom: '10px',
                        padding: '4px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      ← {t('menuCategoryChart.backToCategories')}
                    </button>
                    <h4 style={{ 
                      fontSize: isLaptop ? '13px' : '15px',
                      margin: '0 0 8px 0',
                      color: '#333'
                    }}>
                      {selectedCategoryForItems?.category}
                    </h4>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '15px',
                      gap: '8px',
                      flexWrap: 'nowrap'
                    }}>
                      <div style={{ 
                        fontSize: isLaptop ? '9px' : '10px',
                        color: '#666',
                        flexShrink: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {t('menuCategoryChart.selectItemsToCompare')}
                      </div>
                      {itemsData?.activeItems && itemsData.activeItems.length > 0 && (
                        <button
                          onClick={toggleAllItemsInSidebar}
                          style={{
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            border: '1px solid #dee2e6',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            padding: isLaptop ? '2px 4px' : '2px 6px',
                            fontSize: isLaptop ? '8px' : '9px',
                            fontWeight: '600',
                            color: '#495057',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {itemsData.activeItems.length > 0 && Array.from(itemsData.activeItems as string[]).every(item => visibleItems.has(item))
                            ? t('menuCategoryChart.deselectAll')
                            : t('menuCategoryChart.selectAll')}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <div className="menu-category-sidebar-content">
                {viewMode === 'categories' ? (
                  
                  currentGroupCategories.map((category, idx) => {
                    const isVisible = visibleCategories.has(category.menu_category);
                    const color = colors[idx % colors.length];
                    
                    return (
                      <div key={category.menu_category} className="menu-category-sidebar-item">
                        {}
                        <button
                          className={`menu-category-sidebar-toggle ${isVisible ? 'visible' : 'hidden'}`}
                          onClick={() => toggleCategory(category.menu_category)}
                          style={{
                            fontSize: isLaptop ? '11px' : '12px',
                            padding: isLaptop ? '6px 8px' : '8px 10px',
                            marginBottom: '8px',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start'
                          }}
                        >
                          <span
                            className="menu-category-sidebar-color"
                            style={{
                              backgroundColor: color,
                              opacity: isVisible ? 1 : 0.3,
                              width: isLaptop ? '10px' : '12px',
                              height: isLaptop ? '10px' : '12px',
                              borderRadius: '50%',
                              marginRight: isLaptop ? '6px' : '8px',
                              flexShrink: 0
                            }}
                          />
                          <span className="menu-category-sidebar-name">
                            {category.menu_category}
                          </span>
                        </button>
                        
                        {}
                        <button
                          className="menu-category-sidebar-detail"
                          onClick={() => handleDrillDownToItems(category.menu_group, category.menu_category)}
                          disabled={trendLoading}
                          style={{
                            fontSize: isLaptop ? '9px' : '10px',
                            padding: isLaptop ? '3px 6px' : '4px 8px',
                            marginBottom: '10px',
                            borderColor: color,
                            color: color
                          }}
                        >
                          📈 {t('menuCategoryChart.viewCategoryItems')}
                        </button>
                      </div>
                    );
                  })
                ) : (
                  
                  trendLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      {t('menuCategoryChart.loadingItems')}
                    </div>
                  ) : itemsData?.activeItems?.length ? (
                    (itemsData.activeItems as string[]).map((item: string, idx: number) => {
                      const isVisible = visibleItems.has(item);
                      const color = colors[idx % colors.length];
                      
                      return (
                        <div key={item} className="menu-category-sidebar-item">
                          <button
                            className={`menu-category-sidebar-toggle ${isVisible ? 'visible' : 'hidden'}`}
                            onClick={() => toggleItem(item)}
                            style={{
                              fontSize: isLaptop ? '11px' : '12px',
                              padding: isLaptop ? '6px 8px' : '8px 10px',
                              marginBottom: '15px',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start'
                            }}
                          >
                            <span
                              className="menu-category-sidebar-color"
                              style={{
                                backgroundColor: color,
                                opacity: isVisible ? 1 : 0.3,
                                width: isLaptop ? '10px' : '12px',
                                height: isLaptop ? '10px' : '12px',
                                borderRadius: '50%',
                                marginRight: isLaptop ? '6px' : '8px',
                                flexShrink: 0
                              }}
                            />
                            <span className="menu-category-sidebar-name">
                              {item}
                            </span>
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      {t('menuCategoryChart.noItemsAvailable')}
                    </div>
                  )
                )}
              </div>
              
              {}
              {viewMode === 'categories' && (
                <div className="menu-category-sidebar-footer">
                  <button
                    className="menu-category-sidebar-table-button"
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      setTablePos(calcTablePos(e, isiPad ? 600 : (isLaptop ? 650 : 700)));
                      setShowTable(true);
                    }}
                    style={{
                      fontSize: isLaptop ? '10px' : '11px',
                      padding: isLaptop ? '6px 8px' : '8px 10px',
                      width: '100%'
                    }}
                  >
                    {t('menuCategoryChart.viewSalesTable')}
                  </button>
                </div>
              )}
            </div>

            {}
            <div className="menu-category-chart-area">
              <ReactECharts
                ref={catChartRef}
                option={viewMode === 'categories' ? catOption : itemsOption}
                key={`menu-chart-${viewMode}-${selectedGroup}-${selectedCategoryForItems?.category || ''}`} 
                style={{ 
                  height: '100%', 
                  width: '100%' 
                }}
                className="menu-category-echarts"
                opts={{ 
                  renderer: 'canvas',
                  width: 'auto',
                  height: 'auto'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {}
      {showItems && (
        <div
          className="menu-category-trend-overlay"
          onClick={() => setShowItems(false)}
        >
          <div
            className="menu-category-trend-modal"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: '50%',
              top: `${itemsPos.y}px`,
              transform: 'translateX(-50%)',
              width: isLaptop ? '80%' : '85%',
              maxWidth: isLaptop ? '900px' : '1000px'
            }}
          >
            <div className="menu-category-trend-modal-header">
              <h3 className="menu-category-trend-modal-title" style={{ fontSize: isLaptop ? '16px' : '18px' }}>
                {selectedCat
                  ? t('menuCategoryChart.modalItemsTitle', {
                      category: selectedCat.category
                    })
                  : t('menuCategoryChart.modalItemsTitleDefault')}
              </h3>
              <div className="menu-category-trend-modal-subtitle" style={{ fontSize: isLaptop ? '12px' : '14px' }}>
                {t('menuCategoryChart.modalItemsSubtitle')}
              </div>
              <button
                className="menu-category-trend-modal-close"
                onClick={() => setShowItems(false)}
                style={{ fontSize: isLaptop ? '16px' : '18px' }}
              >
                ✕
              </button>
            </div>

            {}
            <div
              className="menu-category-trend-modal-content"
              style={{ position: 'relative', padding: 0, height: isLaptop ? '500px' : '550px' }}
            >

              {}
              <div className="items-legend-control-top">
                <span className="items-legend-hint" style={{ fontSize: isLaptop ? '11px' : '12px' }}>
                  {t('menuCategoryChart.itemsLegendHint')}
                </span>
                <button
                  className="items-toggle-all-button"
                  onClick={toggleAllItems}
                  style={{ 
                    fontSize: isLaptop ? '10px' : '12px',
                    padding: isLaptop ? '3px 8px' : '4px 10px' 
                  }}
                >
                  {allItemsOn
                    ? t('menuCategoryChart.deselectAll')
                    : t('menuCategoryChart.selectAll')}
                </button>
              </div>

              {trendLoading ? (
                <div className="menu-category-trend-loading">
                  <div className="menu-category-trend-loader" />
                  <div style={{ fontSize: isLaptop ? '14px' : '16px' }}>
                    {t('menuCategoryChart.loadingItemsTrend')}
                  </div>
                </div>
              ) : trendError ? (
                <div className="menu-category-trend-error">
                  <div className="menu-category-trend-error-icon">⚠️</div>
                  <div style={{ fontSize: isLaptop ? '14px' : '16px' }}>
                    {trendError instanceof Error ? trendError.message : t('menuCategoryChart.itemsTrendError')}
                  </div>
                </div>
              ) : !itemsData || !itemsData.monthlyData?.length ? (
                <div className="menu-category-trend-error">
                  <div className="menu-category-trend-error-icon">📊</div>
                  <div style={{ fontSize: isLaptop ? '14px' : '16px' }}>
                    {t('menuCategoryChart.itemsTrendNotFound', { category: selectedCat?.category || '' })}
                  </div>
                </div>
              ) : (
                <ReactECharts
                  ref={itemsChartRef}
                  option={itemsOption}
                  style={{ height: '100%', width: '100%' }}
                  className="menu-category-trend-chart"
                  opts={{ renderer: 'canvas' }}
                  key={`items-${selectedCat?.group}-${selectedCat?.category}`}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {}
      {showTable && (
        <div
          className="menu-category-sales-table-overlay"
          onClick={() => setShowTable(false)}
        >
          <div
            className="menu-category-sales-table-modal"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: '50%',
              top: `${tablePos.y}px`,
              transform: 'translateX(-50%)',
              width: isLaptop ? '95%' : '95%',
              maxWidth: isLaptop ? '1500px' : '1800px'
            }}
          >
            <div className="menu-category-sales-table-modal-header">
              <h3 className="menu-category-sales-table-modal-title" style={{ fontSize: isLaptop ? '16px' : '18px' }}>
                {t('menuCategoryChart.salesTableTitle')}
              </h3>
              <button
                className="menu-category-sales-table-modal-close"
                onClick={() => setShowTable(false)}
                style={{ fontSize: isLaptop ? '16px' : '18px' }}
              >
                ✕
              </button>
            </div>
            <div className="menu-category-sales-table-modal-content" style={{ 
              height: isiPad ? '600px' : (isLaptop ? '520px' : '580px'),
              maxHeight: isiPad ? '600px' : (isLaptop ? '70vh' : '75vh')
            }}>
              <MonthlyItemSalesChart />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuCategoryTrendChart;